import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../services/authApi';
import {
  fetchCarrierUsers, fetchQuoteAssignments, assignQuoteSubCarriers,
  SubCarrierApiError, CarrierUser, Quote,
} from '../../services/carrierApi';

/* Assign one quote to any number of the carrier's SubCarriers.

   Selection is local until Save; Save sends the complete list of checked
   userIds, so anyone left unchecked is unassigned by the server. Both lists
   are fetched on every open — nothing is cached. */

interface Props {
  quote: Quote;
  onClose: () => void;
  /** Called with the names assigned right now — on open (what the server holds)
   *  and again after a successful save — so the table cell stays truthful. */
  onAssignmentChange: (quote: Quote, names: string[]) => void;
  notify: (severity: 'success' | 'error', detail: string) => void;
}

const sameSet = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every(id => b.has(id));

const statusOf = (err: any): number | undefined => err?.status ?? err?.response?.status;

const AssignSubCarriersModal: React.FC<Props> = ({ quote, onClose, onAssignmentChange, notify }) => {
  const navigate = useNavigate();
  const quoteId = quote.databaseId || '';

  const [users, setUsers] = useState<CarrierUser[]>([]);
  const [loaded, setLoaded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // The latest load wins; an older one resolving late must not overwrite it.
  const loadSeq = useRef(0);
  // Parents pass fresh callbacks every render; keep them out of load's deps.
  const onChangeRef = useRef(onAssignmentChange);
  onChangeRef.current = onAssignmentChange;

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login');
  }, [navigate]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [all, assignedIds] = await Promise.all([
        fetchCarrierUsers(),
        fetchQuoteAssignments(quoteId),
      ]);
      if (seq !== loadSeq.current) return;

      const ids = new Set(assignedIds.map(a => a.id));
      setUsers(all.data);
      setLoaded(ids);
      setSelected(new Set(ids));
      onChangeRef.current(quote, all.data.filter(u => ids.has(u.id)).map(u => u.fullName));
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      if (statusOf(err) === 401) return handleUnauthorized();
      setLoadError(err instanceof SubCarrierApiError
        ? err.message
        : 'Could not load users. Please try again.');
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, handleUnauthorized]);

  useEffect(() => {
    load();
    // loadSeq is a counter, not a DOM ref: bumping it on unmount drops any in-flight load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { loadSeq.current++; };
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isSaving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isSaving]);

  const toggle = (id: string) => {
    setSaveError(null);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = users.length > 0 && selected.size === users.length;
  const toggleAll = () => {
    setSaveError(null);
    setSelected(allSelected ? new Set() : new Set(users.map(u => u.id)));
  };

  const dirty = !sameSet(selected, loaded);

  const save = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const userIds = users.filter(u => selected.has(u.id)).map(u => Number(u.id));
      await assignQuoteSubCarriers(quoteId, userIds);

      const names = users.filter(u => selected.has(u.id)).map(u => u.fullName);
      onAssignmentChange(quote, names);
      notify('success', names.length === 0
        ? `${quote.id} is now unassigned.`
        : `${quote.id} assigned to ${names.length === 1 ? names[0] : `${names.length} users`}.`);
      onClose();
    } catch (err: any) {
      const status = statusOf(err);
      if (status === 401) return handleUnauthorized();
      if (status === 404) {
        await load();
        setSaveError('One or more users no longer exist. The list has been refreshed.');
      } else {
        notify('error', err?.message || 'Could not save the assignment. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const goToManageUsers = () => {
    onClose();
    navigate('/carrier/users');
  };

  const noSubCarriers = !isLoading && !loadError && users.length === 0;

  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={e => { if (e.target === e.currentTarget && !isSaving) onClose(); }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Assign users">
        <div className="modal-card__header">
          <div className="assign-head">
            <h3>Assign users</h3>
            <p className="assign-head__quote">
              <span className="quote-id">{quote.id}</span>
              <span className="assign-head__name">{quote.insuredName}</span>
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} disabled={isSaving} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-form">
          {isLoading && <p className="assign-state">Loading users…</p>}

          {loadError && (
            <div className="form-error" role="alert">
              <span>{loadError}</span>
              <button type="button" className="assign-retry" onClick={load}>Try again</button>
            </div>
          )}

          {noSubCarriers && (
            <div className="assign-empty">
              <p>No users yet</p>
              <button type="button" className="btn-primary btn-primary--sm" onClick={goToManageUsers}>
                Go to Manage Users
              </button>
            </div>
          )}

          {!isLoading && users.length > 0 && (
            <>
              <div className="assign-toolbar">
                <span className="assign-toolbar__count">{selected.size} selected</span>
                <button type="button" className="assign-toolbar__link" onClick={toggleAll}>
                  {allSelected ? 'Clear' : 'Select all'}
                </button>
              </div>

              <ul className="assign-list">
                {users.map(u => (
                  <li key={u.id}>
                    <label className={`assign-row${u.isActive ? '' : ' assign-row--inactive'}`}>
                      <input
                        type="checkbox"
                        className="table-checkbox"
                        checked={selected.has(u.id)}
                        disabled={isSaving}
                        onChange={() => toggle(u.id)}
                      />
                      <span className="assign-row__text">
                        <span className="assign-row__name">{u.fullName}</span>
                        <span className="assign-row__email">{u.email}</span>
                      </span>
                      {!u.isActive && <span className="badge badge--rejected">Inactive</span>}
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}

          {saveError && (
            <div className="form-error" role="alert">{saveError}</div>
          )}
        </div>

        <div className="modal-card__footer">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={isSaving || isLoading || !!loadError || !quoteId || !dirty}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignSubCarriersModal;
